import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  collection,
  query,
  getDocs,
  orderBy,
  doc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuthStore } from '../stores/authStore';
import { COLLECTIONS, SUBCOLLECTIONS } from '../lib/collections';
import { createBusinessPlan } from '../lib/functions';
import AnimatedPage from '../components/shared/AnimatedPage';
import LoadingScreen from '../components/shared/LoadingScreen';
import PitchDeckModal from '../components/shared/PitchDeckModal';
import type { BusinessPlan } from '../types/businessPlan';

type Tab = 'in-progress' | 'completed';

function stagesCompleted(plan: BusinessPlan): number {
  return plan.completedStages?.length ?? 0;
}

function isPlanCompleted(plan: BusinessPlan): boolean {
  return plan.currentStage === 'completed' || stagesCompleted(plan) >= 6;
}

function getPlanDocRef(accountId: string, profileId: string, planId: string) {
  return doc(
    db,
    COLLECTIONS.USERS,
    accountId,
    SUBCOLLECTIONS.PROFILES,
    profileId,
    SUBCOLLECTIONS.BUSINESS_PLANS,
    planId,
  );
}

function PencilIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function PlanCard({
  plan,
  isEditing,
  draftName,
  onPencilClick,
  onDraftChange,
  onRenameConfirm,
  onViewPitchDeck,
}: {
  plan: BusinessPlan;
  isEditing: boolean;
  draftName: string;
  onPencilClick: () => void;
  onDraftChange: (v: string) => void;
  onRenameConfirm: () => void;
  onViewPitchDeck?: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const completed = isPlanCompleted(plan);
  const done = stagesCompleted(plan);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  return (
    <div
      className="flex items-center justify-between gap-3 bg-white"
      style={{
        borderRadius: 12,
        border: '0.5px solid #e5e7eb',
        padding: '1rem 1.25rem',
      }}
    >
      {/* Left */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {isEditing ? (
            <input
              ref={inputRef}
              value={draftName}
              onChange={(e) => onDraftChange(e.target.value)}
              onBlur={onRenameConfirm}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Escape') {
                  e.preventDefault();
                  onRenameConfirm();
                }
              }}
              className="min-w-0 flex-1 rounded-lg border border-gray-300 px-2 py-0.5 text-base font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#d94f1e]/30"
            />
          ) : (
            <h3 className="truncate text-base font-bold text-gray-900">
              {plan.businessName || 'Untitled Plan'}
            </h3>
          )}
          {completed && !isEditing && (
            <span
              className="shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold"
              style={{ backgroundColor: '#e6f3e1', color: '#2e7d32' }}
            >
              Completed
            </span>
          )}
        </div>
        <p className="mt-0.5 text-sm text-gray-500">by {plan.explorerName}</p>

        {/* Pip progress */}
        <div className="mt-2.5 flex items-center gap-2">
          <div className="flex gap-1">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                style={{
                  width: 22,
                  height: 5,
                  borderRadius: 3,
                  backgroundColor: i < done ? '#d94f1e' : '#e5e7eb',
                }}
              />
            ))}
          </div>
          <span className="text-xs text-gray-400">{done} / 6 stages</span>
        </div>
      </div>

      {/* Right: buttons */}
      <div className="flex shrink-0 items-center gap-1.5">
        <button
          onClick={onPencilClick}
          title="Rename plan"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
        >
          <PencilIcon />
        </button>

        {completed ? (
          <>
            <button
              onClick={onViewPitchDeck}
              className="rounded-lg border px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              style={{ borderColor: '#d4d4d4' }}
            >
              Pitch Deck
            </button>
            <Link
              to={`/launchpad/${plan.id}`}
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#d94f1e' }}
            >
              View Plan
            </Link>
          </>
        ) : (
          <>
            <Link
              to={`/launchpad/${plan.id}`}
              className="rounded-lg border px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              style={{ borderColor: '#d4d4d4' }}
            >
              Edit
            </Link>
            <Link
              to={`/launchpad/${plan.id}`}
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#d94f1e' }}
            >
              Continue
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export default function MyPlansPage() {
  const navigate = useNavigate();
  const accountId = useAuthStore((s) => s.accountId);
  const profile = useAuthStore((s) => s.profile);

  const [plans, setPlans] = useState<BusinessPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(false);
  const [pitchDeckPlan, setPitchDeckPlan] = useState<BusinessPlan | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('in-progress');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');

  useEffect(() => {
    if (!accountId || !profile) return;

    const plansRef = collection(
      db,
      COLLECTIONS.USERS,
      accountId,
      SUBCOLLECTIONS.PROFILES,
      profile.id,
      SUBCOLLECTIONS.BUSINESS_PLANS,
    );
    const q = query(plansRef, orderBy('createdAt', 'desc'));

    getDocs(q)
      .then((snapshot) => {
        const result: BusinessPlan[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as BusinessPlan;
          if (!data.isDeleted) result.push({ ...data, id: docSnap.id });
        });
        setPlans(result);
      })
      .finally(() => setLoading(false));
  }, [accountId, profile]);

  const handleNewPlan = useCallback(async () => {
    if (!profile || creating) return;
    setCreating(true);
    setCreateError(false);

    const timeout = setTimeout(() => {
      setCreating(false);
      setCreateError(true);
    }, 15000);

    try {
      const result = await createBusinessPlan({
        profileId: profile.id,
        explorerName: profile.explorerName ?? profile.displayName ?? 'Explorer',
      });
      clearTimeout(timeout);
      navigate(`/launchpad/${result.data.planId}`);
    } catch (err) {
      clearTimeout(timeout);
      console.error('Error creating plan:', err);
      setCreating(false);
      setCreateError(true);
    }
  }, [profile, creating, navigate]);

  const handlePencilClick = useCallback((plan: BusinessPlan) => {
    setEditingId(plan.id);
    setDraftName(plan.businessName || '');
  }, []);

  const handleRenameConfirm = useCallback(
    async (plan: BusinessPlan) => {
      if (!accountId || !profile) return;
      const trimmed = draftName.trim();
      setEditingId(null);

      if (!trimmed || trimmed === plan.businessName) return;

      setPlans((prev) =>
        prev.map((p) => (p.id === plan.id ? { ...p, businessName: trimmed } : p)),
      );

      try {
        const docRef = getPlanDocRef(accountId, profile.id, plan.id);
        await updateDoc(docRef, {
          businessName: trimmed,
          lastModified: serverTimestamp(),
        });
      } catch (err) {
        console.error('Error renaming plan:', err);
        // revert
        setPlans((prev) =>
          prev.map((p) => (p.id === plan.id ? { ...p, businessName: plan.businessName } : p)),
        );
      }
    },
    [accountId, profile, draftName],
  );

  if (loading) return <LoadingScreen />;

  const activePlans = plans.filter((p) => !isPlanCompleted(p));
  const completedPlans = plans.filter((p) => isPlanCompleted(p));
  const tabPlans = activeTab === 'in-progress' ? activePlans : completedPlans;

  return (
    <AnimatedPage>
      <div className="min-h-screen bg-gradient-to-b from-brand-50/50 to-white">
        <div className="mx-auto max-w-3xl px-4 py-8">

          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Plans</h1>
              <p className="mt-0.5 text-sm text-gray-500">
                {plans.length === 0
                  ? 'Start your first business adventure'
                  : `${plans.length} plan${plans.length === 1 ? '' : 's'} total`}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <button
                onClick={handleNewPlan}
                disabled={creating}
                className="rounded-xl px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: '#d94f1e' }}
              >
                {creating ? 'Creating…' : '+ New plan'}
              </button>
              {createError && (
                <p className="text-xs text-red-500">Something went wrong. Try again.</p>
              )}
            </div>
          </div>

          {plans.length === 0 ? (
            /* Global empty state */
            <div className="mt-20 flex flex-col items-center text-center">
              <div className="text-7xl">🚀</div>
              <h2 className="mt-4 text-xl font-bold text-gray-900">No plans yet!</h2>
              <p className="mt-2 max-w-xs text-gray-500">
                Build your first business plan and turn your idea into reality.
              </p>
              <button
                onClick={handleNewPlan}
                disabled={creating}
                className="mt-6 rounded-xl px-6 py-3 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: '#d94f1e' }}
              >
                {creating ? 'Creating…' : 'Start a Plan'}
              </button>
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="mt-6 flex gap-6 border-b border-gray-200">
                {([
                  { key: 'in-progress', label: 'In progress', count: activePlans.length },
                  { key: 'completed', label: 'Completed', count: completedPlans.length },
                ] as { key: Tab; label: string; count: number }[]).map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className="relative flex items-center gap-2 pb-3 text-sm font-medium transition-colors"
                    style={{ color: activeTab === tab.key ? '#d94f1e' : '#6b7280' }}
                  >
                    {tab.label}
                    <span
                      className="rounded-full px-1.5 py-0.5 text-xs font-semibold"
                      style={
                        activeTab === tab.key
                          ? { backgroundColor: '#fde8e2', color: '#d94f1e' }
                          : { backgroundColor: '#f3f4f6', color: '#6b7280' }
                      }
                    >
                      {tab.count}
                    </span>
                    {activeTab === tab.key && (
                      <span
                        className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full"
                        style={{ backgroundColor: '#d94f1e' }}
                      />
                    )}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div className="mt-4 space-y-2.5">
                {tabPlans.length === 0 ? (
                  <div className="py-16 text-center text-gray-400">
                    {activeTab === 'in-progress'
                      ? 'No plans in progress. Start a new one!'
                      : 'No completed plans yet. Keep going!'}
                  </div>
                ) : (
                  tabPlans.map((plan) => (
                    <PlanCard
                      key={plan.id}
                      plan={plan}
                      isEditing={editingId === plan.id}
                      draftName={draftName}
                      onPencilClick={() => handlePencilClick(plan)}
                      onDraftChange={setDraftName}
                      onRenameConfirm={() => handleRenameConfirm(plan)}
                      onViewPitchDeck={() => setPitchDeckPlan(plan)}
                    />
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {pitchDeckPlan && (
        <PitchDeckModal
          plan={pitchDeckPlan}
          onClose={() => setPitchDeckPlan(null)}
        />
      )}
    </AnimatedPage>
  );
}
